import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncablePriceListItem } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { PriceListItem as RxPriceListItem } from "../collections/price-list-item";
import type { RxDBCollections } from "../provider";
import {
  SyncPriceListItemPullDocument,
  type WholeSyncablePriceListItemFragment,
} from "./__generated__/price-list-item.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePriceListItem = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxPriceListItem>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncablePriceListItemFragment> | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncPriceListItemPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  return useGqlReplication<RxPriceListItem, SyncablePriceListItem, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlPriceListItemToRxPriceListItem,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "price-list-item",
  });
};

export const mapGqlPriceListItemToRxPriceListItem = (
  item: WholeSyncablePriceListItemFragment,
): WithDeleted<RxPriceListItem> => ({
  id: item.id,
  active: item.active,
  meta: {
    createdAt: item.meta.createdAt,
    createdByUserId: item.meta.createdByUserId,
    updatedAt: item.meta.updatedAt,
    updatedByUserId: item.meta.updatedByUserId,
  },
  _deleted: item.meta.deleted,
});

gql`
  fragment WholeSyncablePriceListItem on SyncablePriceListItem {
    id
    active
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncPriceListItemPull($input: PullPriceListItemInput!) {
    pullPriceListItem(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePriceListItem
      }
    }
  }
`;
